import { useState } from 'react';
import { useDeepSignal } from '../context';
import { buildVariableRowsFromApi, buildCellQuotesFromApi } from '../builders';
import { getModelLanes } from '../model-lanes';
import { SectionLabel, SectionTitle, DSLoadingRow, DSEmptyState } from '../components/section-primitives';
import { WhatWeTestedPanel } from '../components/what-we-tested-panel';
import { VariableScoreChart } from '../components/variable-score-chart';

export function VariableIntelligenceSection({ onAskAI, onOpenBelief }: {
  onAskAI?: (q: string) => void;
  /** Opens the belief page: where this belief surfaces across the series. */
  onOpenBelief?: (beliefId: string) => void;
}) {
  const { apiData, loading } = useDeepSignal();

  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ row: string; col: number } | null>(null);
  const handleRowClick = (rowName: string) => {
    setExpandedRow(prev => prev === rowName ? null : rowName);
    setSelectedCell(null);
  };

  const handleCellClick = (e: React.MouseEvent, rowName: string, colIndex: number) => {
    e.stopPropagation();
    setSelectedCell(prev =>
      prev?.row === rowName && prev?.col === colIndex ? null : { row: rowName, col: colIndex }
    );
    setExpandedRow(rowName);
  };

  if (loading) return (
    <div style={{ padding: '32px 64px' }}>
      {[1,2,3,4,5,6,7].map(i => <DSLoadingRow key={i} />)}
    </div>
  );
  if (!apiData) return <DSEmptyState message="No scoring data available for this brand." />;

  const rows   = buildVariableRowsFromApi(apiData);
  const quotes = buildCellQuotesFromApi(apiData);
  // Model columns come from whatever the run actually measured, so the grid
  // adapts to any count. Cells (from the builder) use the same ordering.
  const lanes = getModelLanes(Object.keys(apiData.model_breakdown));
  const gridCols = `220px 56px ${lanes.map(() => '1fr').join(' ')}`;

  return (
    <div style={{ padding: '0' }}>
      <WhatWeTestedPanel brand={apiData.customer.brand_name ?? 'your brand'} variables={apiData.variables} onAskAI={onAskAI} />
      <VariableScoreChart />
      <div style={{ padding: '28px 0' }}>
      {/* Header */}
      <div style={{ paddingLeft: '32px', paddingRight: '32px', marginBottom: '20px' }}>
        <SectionLabel>Brand Attribute Recall</SectionLabel>
        <SectionTitle size="xl">{`How does each model understand the brand attributes ${apiData.customer.brand_name ?? 'your brand'} wants to encode?`}</SectionTitle>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.5' }}>
          Score 0 to 100, from a 0/1/2 per-response rubric: 0 = absent · 1 = mentioned generically · 2 = stated explicitly as a verifiable claim. Deeper color = stronger encoding.
          <span style={{ marginLeft: '12px', fontStyle: 'italic' }}>Click a row to expand · click a cell for model-specific grounding</span>
        </p>
      </div>

      {/* Full-width heatmap table */}
      <div style={{ paddingLeft: '32px', paddingRight: '32px' }}>

        {/* Column headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          gap: '6px',
          paddingBottom: '10px',
          borderBottom: '1px solid var(--border)',
          alignItems: 'end',
        }}>
          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: '0.2px' }}>Variable</div>
          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--muted-foreground)' }}></div>
          {lanes.map((lane, mi) => (
            <div
              key={lane.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                padding: '5px 8px',
                borderRadius: '6px',
                backgroundColor: hoveredCol === mi ? 'var(--muted)' : 'transparent',
                transition: 'background-color 0.15s',
                cursor: 'default',
              }}
            >
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: lane.color, flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: hoveredCol === mi ? 'var(--foreground)' : 'var(--muted-foreground)' }}>{lane.label}</span>
            </div>
          ))}
        </div>

        {/* Rows */}
        {rows.map((row, ri) => {
          const isExpanded = expandedRow === row.name;
          const isHovered = hoveredRow === row.name;

          return (
            <div key={row.name}>
              {/* Main row */}
              <div
                onClick={() => handleRowClick(row.name)}
                onMouseEnter={() => setHoveredRow(row.name)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  gap: '6px',
                  padding: '7px 0',
                  borderBottom: isExpanded ? 'none' : '1px solid var(--border)',
                  backgroundColor: isExpanded ? 'var(--muted)' : isHovered ? 'var(--muted)' : ri % 2 === 0 ? 'var(--background)' : 'var(--card)',
                  cursor: 'pointer',
                  transition: 'background-color 0.12s',
                  borderRadius: isExpanded ? '8px 8px 0 0' : '4px',
                }}
              >
                {/* Variable name + badges */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '4px' }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: 'var(--muted-foreground)',
                    fontSize: 'var(--text-xs)',
                    transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}>›</div>
                  <div>
                    <p style={{ fontSize: 'var(--text-sm)', fontWeight: row.highlight ? 600 : 500, color: 'var(--foreground)', lineHeight: '1.3' }}>
                      {row.name}
                    </p>
                    {row.highlight && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px' }}>
                        <div style={{ width: '5px', height: '5px', borderRadius: '2px', backgroundColor: '#ff6d66' }} />
                        <span style={{ fontSize: 'var(--text-xs)', color: '#ff6d66', fontWeight: 500 }}>ceiling risk</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Spacer (kept for grid alignment with header) */}
                <div />

                {/* Score cells */}
                {row.cells.map((cell, ci) => {
                  const isCellSelected = selectedCell?.row === row.name && selectedCell?.col === ci;
                  const isColHovered = hoveredCol === ci;
                  return (
                    <div
                      key={ci}
                      onClick={(e) => handleCellClick(e, row.name, ci)}
                      onMouseEnter={() => setHoveredCol(ci)}
                      onMouseLeave={() => setHoveredCol(null)}
                      style={{
                        backgroundColor: cell.bg,
                        borderRadius: '6px',
                        padding: '10px 8px 8px',
                        height: '86px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        outline: isCellSelected ? `2px solid ${lanes[ci]?.color}` : isColHovered ? '1px solid rgba(0,0,0,0.12)' : 'none',
                        outlineOffset: '-2px',
                        transition: 'transform 0.1s, outline 0.1s',
                        transform: isCellSelected ? 'scale(1.04)' : 'scale(1)',
                        zIndex: isCellSelected ? 2 : 1,
                        boxShadow: isCellSelected ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
                      }}
                    >
                      <p style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: cell.textColor, marginBottom: '5px', letterSpacing: '-0.5px' }}>{Math.round(parseFloat(cell.value) / 2 * 100)}</p>
                      <div style={{ width: '80%', height: '3px', backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ width: `${(parseFloat(cell.value) / 2) * 100}%`, height: '100%', backgroundColor: 'rgba(0,0,0,0.18)', borderRadius: '2px' }} />
                      </div>
                      {isCellSelected && (
                        <div style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          width: '7px',
                          height: '7px',
                          borderRadius: '50%',
                          backgroundColor: lanes[ci]?.color,
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Inline expand drawer */}
              {isExpanded && (
                <div style={{
                  borderBottom: '1px solid var(--border)',
                  backgroundColor: 'var(--muted)',
                  borderRadius: '0 0 8px 8px',
                  borderTop: '1px solid rgba(0,201,196,0.12)',
                  padding: '12px 0',
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  gap: '6px',
                }}>
                  {/* Full claim text for the short summary label above */}
                  {onOpenBelief && apiData.variables[ri] && (
                    <div style={{ gridColumn: '1 / -1', padding: '0 12px 8px 30px' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); onOpenBelief(apiData.variables[ri].id); }}
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--alpha-teal)',
                        }}
                      >
                        Where this belief surfaces, question by question →
                      </button>
                    </div>
                  )}
                  {row.definition && (
                    <p style={{
                      gridColumn: '1 / -1',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--muted-foreground)',
                      lineHeight: 1.55,
                      padding: '0 12px 8px 30px',
                      maxWidth: '760px',
                    }}>
                      {row.definition}
                    </p>
                  )}
                  {/* Spacers to align with variable name + badge columns */}
                  <div />
                  <div />
                  {lanes.map((lane, mi) => {
                    const q = quotes[row.name]?.[lane.id];
                    const isSel = selectedCell?.row === row.name && selectedCell?.col === mi;
                    if (!q) return <div key={lane.id} />;
                    return (
                      <div
                        key={lane.id}
                        onClick={(e) => handleCellClick(e, row.name, mi)}
                        style={{
                          backgroundColor: q.noEvidence ? 'var(--muted)' : isSel ? 'var(--background)' : 'var(--card)',
                          border: isSel ? `1.5px solid ${lane.color}` : '1px solid var(--border)',
                          borderRadius: '8px',
                          padding: '12px',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s, box-shadow 0.15s',
                          boxShadow: isSel ? '0 0 0 3px var(--muted)' : 'none',
                          opacity: q.noEvidence ? 0.7 : 1,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: lane.color }} />
                          <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--foreground)' }}>{lane.label}</span>
                        </div>
                        {!q.noEvidence && (
                          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', marginBottom: '4px' }}>
                            Best scored response:
                          </p>
                        )}
                        <p style={{ fontSize: 'var(--text-xs)', color: q.noEvidence ? 'var(--muted-foreground)' : 'var(--foreground)', lineHeight: '1.55', marginBottom: '8px', fontStyle: 'italic' }}>{q.quote}</p>
                        {q.watch && (
                          <div style={{
                            marginTop: '8px',
                            backgroundColor: 'rgba(255,109,102,0.10)',
                            borderRadius: '4px',
                            padding: '7px 9px',
                            fontSize: 'var(--text-xs)',
                            color: '#ff6d66',
                            borderLeft: '2px solid #ff6d66',
                          }}>
                            {q.watch}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Color-band legend removed pending enough data to calibrate real absolute
            thresholds. The header's "deeper color = stronger encoding" carries the
            relative read in the meantime. */}
      </div>

      </div>
    </div>
  );
}
